import type { PluginManifest, ResolvedMenuGroup } from '../../plugins/types';
import type { QuickAccessItem } from '../../contexts/QuickAccessContext';

export type LauncherItemType = 'plugin' | 'action' | 'route' | 'setting';

export type LauncherSearchItem = {
  id: string;
  type: LauncherItemType;
  title: string;
  subtitle?: string;
  description?: string;
  icon?: string;
  route: string;
  group?: string;
  section?: string;
  aliases: string[];
  taskPhrases: string[];
  keywords: string[];
  lastAccessedAt?: number;
  accessCount?: number;
  pinned?: boolean;
  disabled?: boolean;
  disabledReason?: string;
};

export type LauncherResultSections = {
  pinned: LauncherSearchItem[];
  recent: LauncherSearchItem[];
  bestMatch: LauncherSearchItem[];
  otherMatches: LauncherSearchItem[];
  actions: LauncherSearchItem[];
};

export type RankingWeights = {
  exactTitleMatch: number;
  titleStartsWith: number;
  titleContains: number;
  aliasExactMatch: number;
  aliasFuzzyMatch: number;
  taskPhraseMatch: number;
  keywordMatch: number;
  descriptionMatch: number;
  pinnedBoost: number;
  recentBoost: number;
  frequentBoost: number;
  sameGroupBoost: number;
  disabledPenalty: number;
};

export const DEFAULT_RANKING_WEIGHTS: RankingWeights = {
  exactTitleMatch: 100,
  titleStartsWith: 80,
  titleContains: 64,
  aliasExactMatch: 75,
  aliasFuzzyMatch: 60,
  taskPhraseMatch: 55,
  keywordMatch: 40,
  descriptionMatch: 20,
  pinnedBoost: 30,
  recentBoost: 20,
  frequentBoost: 15,
  sameGroupBoost: 10,
  disabledPenalty: -50,
};

export type LauncherContext = {
  currentGroup?: string;
  currentPath?: string;
};

export type LauncherLimits = {
  maxBestMatch: number;
  maxOtherMatch: number;
  maxRecent: number;
  maxPinned: number;
  maxRecommended: number;
  maxActions: number;
  frequentAccessThreshold: number;
  zeroResultMinQueryLength: number;
  undoTimeoutMs: number;
};

export const DEFAULT_LAUNCHER_LIMITS: LauncherLimits = {
  maxBestMatch: 5,
  maxOtherMatch: 10,
  maxRecent: 8,
  maxPinned: 10,
  maxRecommended: 5,
  maxActions: 5,
  frequentAccessThreshold: 3,
  zeroResultMinQueryLength: 3,
  undoTimeoutMs: 5000,
};

export type BuildLauncherIndexInput = {
  manifests: PluginManifest[];
  menuGroups: ResolvedMenuGroup[];
  quickAccessItems: QuickAccessItem[];
  pinnedIds: Set<string>;
};

export interface GlobalLauncherProps {
  open: boolean;
  sections: LauncherResultSections;
  query: string;
  selectedIndex: number;
  totalResultCount: number;
  onQueryChange: (q: string) => void;
  onSelect: (item: LauncherSearchItem) => void;
  onPinToggle: (item: LauncherSearchItem) => void;
  onRemoveRecent?: (pluginId: string) => void;
  onClearRecents?: () => void;
  onRestoreRecent?: (item: LauncherSearchItem) => void;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLElement | null>;
}
